import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { focusQuietly } from '@theme/focus';

let isKeyboardNavigation = false;

document.addEventListener(
  'keydown',
  (event) => {
    if (event.metaKey || event.altKey || event.ctrlKey) return;
    isKeyboardNavigation = true;
  },
  true
);

document.addEventListener(
  'pointerdown',
  () => {
    isKeyboardNavigation = false;
  },
  true
);

document.addEventListener(
  'touchstart',
  () => {
    isKeyboardNavigation = false;
  },
  true
);

document.addEventListener(
  'mousedown',
  () => {
    isKeyboardNavigation = false;
  },
  true
);

/**
 * Whether the most recent user interaction was a keypress rather than a pointer
 * press. Shared with other flows that move focus programmatically (e.g. the
 * product form's "please select a size" prompt) so they can mirror the
 * data-suppress-initial-close-focus pattern: withhold the focus ring when the
 * trigger was pointer-driven, keep it for keyboard users.
 *
 * @returns {boolean}
 */
export function lastInteractionWasKeyboard() {
  return isKeyboardNavigation;
}

const DIALOG_MODAL_SELECTOR = '.dialog-modal';

/**
 * AP: the premium slide-in drawers - the same five-class list base.css repeats for
 * every premium-drawer rule (divergences #26, #45). Keep in sync with base.css.
 */
const PREMIUM_DRAWER_SELECTOR =
  ':is(.drawer__dialog, .cart-drawer__dialog, .facets--drawer, .search-modal__content, .newsletter-drawer__dialog).dialog-drawer';

/**
 * @param {string} value
 * @returns {number}
 */
function parseCSSTime(value) {
  const time = value.trim();

  if (!time) return 0;
  if (time.endsWith('ms')) return Number.parseFloat(time) || 0;
  if (time.endsWith('s')) return (Number.parseFloat(time) || 0) * 1000;

  return Number.parseFloat(time) || 0;
}

/**
 * @param {HTMLDialogElement} dialog
 * @returns {number}
 */
function getBackdropCloseDuration(dialog) {
  if (!dialog.matches(DIALOG_MODAL_SELECTOR)) return 0;

  // AP: base.css collapses the premium drawers' backdrop fade to a single frame
  // under prefers-reduced-motion, but this wait reads the unreduced token, so the
  // page would stay modal-inert for the full overlay duration after an instant
  // close. Scoped to those drawers only: the cookie banner and the centred modals
  // keep their full-length backdrop fade and must keep the matching wait.
  if (prefersReducedMotion() && dialog.matches(PREMIUM_DRAWER_SELECTOR)) return 0;

  return parseCSSTime(getComputedStyle(dialog).getPropertyValue('--drawer-overlay-transition-speed'));
}

/**
 * @param {number} duration
 * @returns {Promise<void>}
 */
function wait(duration) {
  return new Promise((resolve) => setTimeout(resolve, duration));
}

/**
 * AP: the page-level body pin, shared by every DialogComponent.
 *
 * Each open modal used to pin the body itself and remember its own scroll
 * position. A second dialog opening over a first therefore recorded
 * `scrollY = 0` (the body was already fixed), and closing it unpinned the page
 * under the still-open first dialog and scrolled to the top. One
 * reference-counted owner pins on the first acquire and restores on the last
 * release, keeping a single snapshot of the body's original inline styles and
 * of the scroll position to return to.
 */
const bodyLock = {
  count: 0,
  scrollY: 0,
  /** @type {{ width: string, position: string, top: string } | null} */
  styles: null,

  acquire() {
    this.count += 1;
    if (this.count > 1) return;

    const { style } = document.body;
    this.scrollY = window.scrollY;
    this.styles = { width: style.width, position: style.position, top: style.top };
    style.width = '100%';
    style.position = 'fixed';
    style.top = `-${this.scrollY}px`;
  },

  release() {
    if (this.count === 0) return;
    this.count -= 1;
    if (this.count > 0) return;

    const { style } = document.body;
    style.width = this.styles?.width ?? '';
    style.position = this.styles?.position ?? '';
    style.top = this.styles?.top ?? '';
    this.styles = null;
    window.scrollTo({ top: this.scrollY, behavior: 'instant' });
  },
};

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #addedDialogFocusTarget = false;

  /** @type {Promise<void> | null} */
  #closePromise = null;

  connectedCallback() {
    super.connectedCallback();

    // AP: recover from browser-forced native closes — see #handleDialogClose.
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('close', this.#handleDialogClose);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    // AP: symmetric teardown. Drop the pending open frame and the resize
    // debounce so neither fires against a detached element. A modal removed
    // from the document leaves the top layer without a `close` event and keeps
    // its `open` attribute, so if it were reinserted (morph.js moves existing
    // nodes with insertBefore) it would sit there non-modal with the page
    // scrollable and `showDialog()` refusing to reopen it. Close it fully here
    // instead. The `open` attribute is removed directly rather than via
    // `dialog.close()`: removal already took the dialog out of the top layer
    // and cleared its modal state, and `close()` would queue a native `close`
    // event that a synchronous move (remove + insert in one task) reconnects
    // in time to receive, replaying the restore against the reinserted - and
    // possibly reopened - dialog. Removing the attribute queues nothing, so
    // the shared restore runs exactly once and consumers get one `dialog:close`.
    this.#cancelShowFrame();
    this.#handleResize.cancel();

    // Invalidate a close animation still in flight (see #runCloseDialog) and
    // free the dedupe slot so a reinserted dialog can start a fresh close.
    this.#lifecycleGeneration += 1;
    this.#closePromise = null;

    const { dialog } = this.refs;
    if (dialog?.open) {
      dialog.removeAttribute('open');
      this.#restoreAfterClose();
    } else {
      this.#releaseBodyLock();
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** True while this component holds one reference on the shared {@link bodyLock}. */
  #holdsBodyLock = false;

  /** The `requestAnimationFrame` id scheduled by {@link showDialog}, or null once it has run or been cancelled. */
  #showFrame = null;

  /** Bumped on disconnect so an async close continuation from a previous connection does nothing. */
  #lifecycleGeneration = 0;

  /** True only across the deliberate `dialog.close()` at the end of {@link #runCloseDialog}. */
  #expectedClose = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#showFrame !== null) return;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#showFrame = requestAnimationFrame(() => {
      this.#showFrame = null;

      // AP: the frame can outlive the element (a section re-render between the
      // call and the frame). Never pin the body for a dialog that cannot open.
      if (!this.isConnected) return;

      bodyLock.acquire();
      this.#holdsBodyLock = true;

      const shouldSuppressInitialCloseFocus = !isKeyboardNavigation && dialog.matches(PREMIUM_DRAWER_SELECTOR);

      dialog.removeAttribute('data-suppress-initial-close-focus');

      if (shouldSuppressInitialCloseFocus) {
        dialog.setAttribute('data-suppress-initial-close-focus', '');
      }

      dialog.showModal();

      if (shouldSuppressInitialCloseFocus) {
        dialog.setAttribute('tabindex', '-1');
        this.#addedDialogFocusTarget = true;
        dialog.focus({ preventScroll: true });
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   *
   * Idempotent by design: while the exit animation is running the `<dialog>`
   * still reports `open`, so a second close request — a backdrop tap that also
   * fires `popstate`, Escape plus a click, an iOS synthetic click that follows
   * a touch, a resize crossing the breakpoint — would otherwise re-enter
   * {@link #runCloseDialog}, reset and restart the closing animation, and
   * replay it visually. Collapsing every concurrent call into the single
   * in-flight close promise prevents that replay for all drawer/dialog users.
   *
   * @returns {Promise<void>}
   */
  closeDialog = async () => {
    if (this.#closePromise) return this.#closePromise;

    // AP: a close that lands before showDialog's frame has run cancels the open
    // outright - otherwise the frame would still pin the body for a dialog no
    // one asked to keep open.
    if (this.#showFrame !== null) {
      this.#cancelShowFrame();
      return;
    }

    if (!this.refs.dialog.open) return;

    const closePromise = this.#runCloseDialog();
    this.#closePromise = closePromise;

    try {
      await closePromise;
    } finally {
      // AP: only clear our own promise - a disconnect mid-animation resets the
      // field so a reinserted, reopened dialog can start a fresh close.
      if (this.#closePromise === closePromise) this.#closePromise = null;
    }
  };

  /**
   * Performs the actual close and runs the exit animation. Never call this
   * directly; go through {@link closeDialog} so concurrent closes are deduped.
   *
   * @returns {Promise<void>}
   */
  #runCloseDialog = async () => {
    const { dialog } = this.refs;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    const generation = this.#lifecycleGeneration;

    await Promise.allSettled([
      onAnimationEnd(dialog, undefined, {
        subtree: false,
      }),
      wait(getBackdropCloseDuration(dialog)),
    ]);

    // AP: the element was removed while the animation ran. disconnectedCallback
    // already closed and restored it, and a reinsert-and-reopen may own the
    // dialog and a fresh body-lock reference by now - this continuation must
    // not close that or release that.
    if (generation !== this.#lifecycleGeneration) return;

    this.#releaseBodyLock();

    dialog.classList.remove('dialog-closing');
    if (dialog.open) this.#expectedClose = true;

    // AP: the native close hands focus back to the trigger. When the drawer was
    // opened by pointer (the suppression flag is still set - only a key other than
    // Escape clears it), keep that return quiet: Escape is a key, so the browser
    // would otherwise paint a keyboard ring on the trigger a customer tapped.
    const quietReturn = dialog.hasAttribute('data-suppress-initial-close-focus');

    dialog.close();
    dialog.removeAttribute('data-suppress-initial-close-focus');
    if (this.#addedDialogFocusTarget) {
      dialog.removeAttribute('tabindex');
      this.#addedDialogFocusTarget = false;
    }

    const returnedTo = document.activeElement;
    if (quietReturn && returnedTo instanceof HTMLElement && returnedTo !== document.body) {
      focusQuietly(returnedTo);
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * AP: safety net for browser-initiated closes. A modal <dialog> is a close
   * watcher: the Android back gesture (and any future platform close request)
   * can close it natively without going through {@link closeDialog} — that
   * path skips {@link #runCloseDialog}, which is the only place the body
   * pinning from {@link showDialog} is undone, leaving the page unscrollable
   * until reload. When a `close` event arrives that we didn't initiate,
   * restore the page state synchronously. Deliberate closes are marked via
   * {@link #expectedClose} just before their `dialog.close()` call.
   */
  #handleDialogClose = () => {
    if (this.#expectedClose) {
      this.#expectedClose = false;
      return;
    }

    this.#restoreAfterClose();
  };

  /**
   * Restores page and element state after a close that bypassed
   * {@link #runCloseDialog}: a browser-forced native close, or removal from the
   * document while open. Shared so both paths stay in step.
   */
  #restoreAfterClose() {
    const { dialog } = this.refs;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.#releaseBodyLock();

    dialog.classList.remove('dialog-closing');
    dialog.removeAttribute('data-suppress-initial-close-focus');
    if (this.#addedDialogFocusTarget) {
      dialog.removeAttribute('tabindex');
      this.#addedDialogFocusTarget = false;
    }

    this.dispatchEvent(new DialogCloseEvent());
  }

  /** Cancels a {@link showDialog} frame that has not run yet. Safe to call when none is pending. */
  #cancelShowFrame() {
    if (this.#showFrame === null) return;
    cancelAnimationFrame(this.#showFrame);
    this.#showFrame = null;
  }

  /** Gives back this component's reference on the shared {@link bodyLock}. Idempotent. */
  #releaseBodyLock() {
    if (!this.#holdsBodyLock) return;
    this.#holdsBodyLock = false;
    bodyLock.release();
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // AP: Escape closes the dialog, so it keeps the pointer-open suppression - clearing
    // it here made the dialog's own outline appear for the length of the close
    // animation (the "line down the drawer's edge") and let the trigger ring on return.
    // Any other key is real keyboard navigation and restores the rings.
    if (event.key !== 'Escape') {
      this.refs.dialog.removeAttribute('data-suppress-initial-close-focus');
      return;
    }

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
